import { useMemo } from 'react';

import { useStorageCheckoutContext } from '@root/components/checkout/context';

import {
  ChallengerFormSection,
  getChallengerSectionCompletion,
  getChallengerSectionEnabled,
  getFirstIncompleteChallengerSection,
  isChallengerStoragePlanComplete,
} from '../completion';

type SectionGatingArgs = {
  storageEligible: boolean;
  loadingGeocode: boolean;
  geocodeUnavailable: boolean;
  leadToken?: string;
};

/**
 * Sequential section completion + enablement for challenger page 1.
 * Lower sections stay disabled until the section above is complete.
 */
export const useChallengerSectionGating = ({
  storageEligible,
  loadingGeocode,
  geocodeUnavailable,
  leadToken,
}: SectionGatingArgs) => {
  const {
    disposalEligible,
    flowState: { values },
  } = useStorageCheckoutContext();

  const completionArgs = useMemo(
    () => ({
      values,
      disposalEligible,
      leadToken,
      storageEligible,
      loadingGeocode,
      geocodeUnavailable,
    }),
    [
      disposalEligible,
      geocodeUnavailable,
      leadToken,
      loadingGeocode,
      storageEligible,
      values,
    ],
  );

  const sectionCompletion = useMemo(
    () => getChallengerSectionCompletion(completionArgs),
    [completionArgs],
  );

  const sectionEnabled = useMemo(
    () => getChallengerSectionEnabled(sectionCompletion),
    [sectionCompletion],
  );

  const planComplete = useMemo(
    () => isChallengerStoragePlanComplete(completionArgs),
    [completionArgs],
  );

  const firstIncompleteSection = useMemo(
    () => getFirstIncompleteChallengerSection(sectionCompletion),
    [sectionCompletion],
  );

  const isSectionIncomplete = (section: ChallengerFormSection): boolean =>
    !sectionCompletion[section];

  return {
    completionArgs,
    firstIncompleteSection,
    isSectionIncomplete,
    planComplete,
    sectionCompletion,
    sectionEnabled,
  };
};
